/**
 * WebSocket Context Provider
 * Manages a single WebSocket connection for the entire app
 * Connection is established when user logs in and has valid tokens
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAppSelector } from '../../../layouts/App/hooks/useAppSelector';
import { areTokensValid } from '../../auth/utils/tokenCache';
import { useWebSocket as useWebSocketHook, WebSocketMessage, UseWebSocketReturn } from '../hooks/useWebSocket';

interface WebSocketContextType extends UseWebSocketReturn {
  // Subscribe to messages
  subscribe: (callback: (message: WebSocketMessage) => void) => () => void;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

// Store message subscribers
const messageSubscribers = new Set<(message: WebSocketMessage) => void>();

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { isLoggedIn } = useAppSelector((state) => state.auth);
  const [shouldConnect, setShouldConnect] = useState(false);

  // Check if we should connect (user is logged in and has valid tokens)
  useEffect(() => {
    if (isLoggedIn) {
      // Check if tokens are valid
      const hasValidTokens = areTokensValid();
      if (hasValidTokens) {
        console.log('✅ User is logged in with valid tokens, establishing WebSocket connection...');
        setShouldConnect(true);
      } else {
        console.log('⚠️ User is logged in but tokens are invalid, not connecting WebSocket');
        setShouldConnect(false);
      }
    } else {
      console.log('ℹ️ User is not logged in, WebSocket connection disabled');
      setShouldConnect(false);
    }
  }, [isLoggedIn]);

  // Handle incoming WebSocket messages - forward to all subscribers
  const handleMessage = useCallback((message: WebSocketMessage) => {
    console.log('📨 WebSocket message received in context:', message);
    // Forward to all subscribers
    messageSubscribers.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('❌ Error in message subscriber:', error);
      }
    });
  }, []);

  // Use WebSocket hook - it will handle connection/disconnection based on shouldConnect
  const webSocketData = useWebSocketHook(
    shouldConnect ? handleMessage : undefined, 
    shouldConnect
  );

  // Subscribe function to allow components to listen to messages
  const subscribe = useCallback((callback: (message: WebSocketMessage) => void) => {
    messageSubscribers.add(callback);
    // Return unsubscribe function
    return () => {
      messageSubscribers.delete(callback);
    };
  }, []);

  // Disconnect when user logs out
  useEffect(() => {
    if (!isLoggedIn && webSocketData.isConnected) {
      console.log('🔌 User logged out, disconnecting WebSocket...');
      webSocketData.disconnect();
    }
  }, [isLoggedIn, webSocketData.isConnected, webSocketData.disconnect]);

  const value: WebSocketContextType = {
    ...webSocketData,
    subscribe
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use WebSocket context
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};
